import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Save, ChevronDown, Info } from "lucide-react";
import { sessionService } from "../../../application/SessionApplicationService";

interface TranscriptionTurn {
  speaker: string;
  text: string;
}

function parseTranscription(raw: string): TranscriptionTurn[] {
  const lines = raw.split("\n").filter((l) => l.trim());
  const turns: TranscriptionTurn[] = [];
  for (const line of lines) {
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) {
      if (turns.length > 0) turns[turns.length - 1].text += " " + line.trim();
      continue;
    }
    const speaker = line.slice(0, colonIdx).trim();
    const text = line.slice(colonIdx + 1).trim();
    if (speaker && text) turns.push({ speaker, text });
  }
  return turns;
}

function serializeTranscription(turns: TranscriptionTurn[]): string {
  return turns.map((t) => `${t.speaker}: ${t.text}`).join("\n");
}

function detectLanguage(turns: TranscriptionTurn[]): "es" | "en" {
  const labels = turns.map((t) => t.speaker.toLowerCase()).join(" ");
  return labels.includes("terapeuta") || labels.includes("paciente") ? "es" : "en";
}

function getSpeakerOptions(isCouple: boolean, lang: "es" | "en"): string[] {
  if (lang === "es") {
    return isCouple ? ["Terapeuta", "Paciente 1", "Paciente 2"] : ["Terapeuta", "Paciente"];
  }
  return isCouple ? ["Therapist", "Patient 1", "Patient 2"] : ["Therapist", "Patient"];
}

function getSpeakerRole(speaker: string): "therapist" | "patient1" | "patient2" | "other" {
  const lower = speaker.toLowerCase();
  if (lower.includes("therapist") || lower.includes("terapeuta")) return "therapist";
  if (lower.includes("patient 2") || lower.includes("paciente 2") || lower.includes("speaker 2") || lower.includes("hablante 2")) return "patient2";
  if (lower.includes("patient") || lower.includes("paciente") || lower.includes("speaker 1") || lower.includes("hablante 1")) return "patient1";
  return "other";
}

const roleStyles = {
  therapist: {
    bubble: "bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800/30",
    label: "text-brand-600 dark:text-brand-400",
    labelHover: "hover:bg-brand-100 dark:hover:bg-brand-800/30",
    align: "items-start",
  },
  patient1: {
    bubble: "bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5",
    label: "text-content-tertiary",
    labelHover: "hover:bg-gray-100 dark:hover:bg-white/10",
    align: "items-end",
  },
  patient2: {
    bubble: "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30",
    label: "text-emerald-600 dark:text-emerald-400",
    labelHover: "hover:bg-emerald-100 dark:hover:bg-emerald-800/30",
    align: "items-end",
  },
  other: {
    bubble: "bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/5",
    label: "text-content-tertiary",
    labelHover: "hover:bg-gray-100 dark:hover:bg-white/10",
    align: "items-end",
  },
};

interface SessionViewTranscriptionProps {
  transcription: string;
  sessionId: string;
  sessionType?: string;
}

export default function SessionViewTranscription({
  transcription,
  sessionId,
  sessionType,
}: SessionViewTranscriptionProps) {
  const { t } = useTranslation();
  const isCouple = sessionType === "couple";

  const [turns, setTurns] = useState<TranscriptionTurn[]>(() => parseTranscription(transcription));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  const lang = detectLanguage(turns);
  const speakerOptions = getSpeakerOptions(isCouple, lang);

  const changeSpeaker = useCallback((idx: number, newSpeaker: string) => {
    setTurns((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], speaker: newSpeaker };
      return updated;
    });
    setDirty(true);
    setOpenDropdown(null);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await sessionService.updateSession(sessionId, { transcription: serializeTranscription(turns) });
      setDirty(false);
    } catch (err) {
      console.error("Error saving transcription:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!transcription || !transcription.trim()) {
    return (
      <div className="glass-effect glass-border border rounded-2xl p-8 shadow-theme-sm">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="w-5 h-5 text-content-tertiary" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {t("session.transcription", "Transcription")}
          </h3>
        </div>
        <p className="text-content-tertiary italic text-sm">
          {t("session.transcriptionEmpty", "No transcription available. Transcription is generated automatically when audio is processed.")}
        </p>
      </div>
    );
  }

  return (
    <div className="glass-effect glass-border border rounded-2xl p-8 shadow-theme-sm">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="w-5 h-5 text-brand-500" />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {t("session.transcription", "Transcription")}
        </h3>
        <span className="text-xs text-content-tertiary">
          {turns.length} {t("session.transcriptionTurns", "turns")}
        </span>
        {dirty ? (
          <button
            onClick={handleSave}
            disabled={saving}
            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold transition-all disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </button>
        ) : (
          <span className="ml-auto text-xs text-content-tertiary italic">
            {t("session.transcriptionClickLabel", "Click a name to reassign")}
          </span>
        )}
      </div>

      <div className="flex items-start gap-2.5 mb-5 px-4 py-3 rounded-xl bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40">
        <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
          {t(
            "session.transcriptionAIWarning",
            "This transcription was generated by AI and speaker labels may not be accurate. Click any speaker name to reassign it to the correct role."
          )}
        </p>
      </div>

      {openDropdown !== null && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
      )}

      <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
        {turns.map((turn, idx) => {
          const role = getSpeakerRole(turn.speaker);
          const styles = roleStyles[role];
          const isOpen = openDropdown === idx;

          return (
            <div key={idx} className={`flex flex-col gap-1 ${styles.align}`}>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(isOpen ? null : idx);
                  }}
                  className={`flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg transition-all ${styles.label} ${styles.labelHover}`}
                >
                  {turn.speaker}
                  <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="absolute z-20 top-full mt-1 left-0 bg-surface-raised border border-gray-100 dark:border-white/10 rounded-xl shadow-theme-xl overflow-hidden min-w-[140px]">
                    {speakerOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={(e) => {
                          e.stopPropagation();
                          changeSpeaker(idx, opt);
                        }}
                        className={`w-full text-left px-4 py-2 text-xs font-semibold transition-colors ${
                          opt === turn.speaker
                            ? "bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 leading-relaxed ${styles.bubble}`}>
                {turn.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
